import {
  FieldDefinition,
  GenerateFieldsOptions,
} from '@/types/fields-api.model';

/**
 * Recursively generates a fields query string from a field definition object
 *
 * @param fields - The field definition object
 * @param options - Generation options
 * @returns The fields query string
 *
 * @example
 * ```typescript
 * const fields = {
 *   name: true,
 *   address: {
 *     city: true,
 *     street: true
 *   }
 * };
 *
 * generateFields(fields) // "name,address(city,street)"
 * ```
 */
export function generateFields(
  fields: FieldDefinition,
  options: GenerateFieldsOptions = {},
): string {
  const { sort = false, separator = ',' } = options;

  function processFields(obj: FieldDefinition): string[] {
    const result: string[] = [];

    const entries = Object.entries(obj);
    const sortedEntries = sort
      ? entries.sort(([a], [b]) => a.localeCompare(b))
      : entries;

    for (const [key, value] of sortedEntries) {
      if (value === true) {
        result.push(key);
      } else if (typeof value === 'object' && value !== null) {
        const nestedFields = processFields(value);
        if (nestedFields.length > 0) {
          result.push(`${key}(${nestedFields.join(separator)})`);
        }
      }
    }

    return result;
  }

  const fieldArray = processFields(fields);
  return fieldArray.join(separator);
}

/**
 * Merges multiple field definitions into one
 * Later definitions override earlier ones
 *
 * @param fieldDefinitions - Array of field definitions to merge
 * @returns Merged field definition
 */
export function mergeFields(
  ...fieldDefinitions: FieldDefinition[]
): FieldDefinition {
  function deepMerge(
    target: FieldDefinition,
    source: FieldDefinition,
  ): FieldDefinition {
    const result = { ...target };

    for (const [key, value] of Object.entries(source)) {
      if (value === true || value === false) {
        result[key] = value;
      } else if (typeof value === 'object' && value !== null) {
        if (
          typeof result[key] === 'object' &&
          result[key] !== null &&
          !result[key]
        ) {
          result[key] = deepMerge(result[key] as FieldDefinition, value);
        } else {
          result[key] = { ...value };
        }
      }
    }

    return result;
  }

  return fieldDefinitions.reduce((acc, fields) => deepMerge(acc, fields), {});
}

/**
 * Creates a field definition with only the specified fields enabled
 * Useful for creating minimal scopes
 *
 * @param fields - Array of field names to include
 * @returns Field definition with only specified fields
 */
export function createMinimalFields(fields: string[]): FieldDefinition {
  return fields.reduce((acc, field) => {
    acc[field] = true;
    return acc;
  }, {} as FieldDefinition);
}
